import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { api } from './lib/api';
import { syncMoneyMode } from './lib/money';
import { syncSidebarLayout } from './lib/sidebarLayout';
import { clearMeetingCaches } from './lib/meetings';
import Meetings, { MeetingDetailPage } from './pages/Meetings';
import { clearOverviewCaches } from './lib/overviewCache';
import { clearNavState, getSessionStorage } from './lib/viewState';
import Shell from './components/Shell';
import Login from './pages/Login';
import Setup from './pages/Setup';
import Overview from './pages/Overview';
import Settings from './pages/Settings';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Outreach from './pages/Outreach';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Crew from './pages/Crew';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Tasks from './pages/Tasks';
import Finance from './pages/Finance';
import InvoiceDetail from './pages/InvoiceDetail';
import Stats from './pages/Stats';
import Dashboards from './pages/Dashboards';
import DashboardEdit from './pages/DashboardEdit';
import Social from './pages/Social';
import Documents from './pages/Documents';
const Systems = lazy(() => import('./pages/Systems/Systems'));
const Marketing = lazy(() => import('./pages/Marketing/Marketing'));
import ToolEmbed from './pages/ToolEmbed';
import Automations from './pages/Automations';
import Drives from './pages/Drives';

interface AuthStatus {
  needsTotp: boolean;
}

interface AuthMe {
  ok: true;
  user: { id: number };
}

export type AuthState =
  | { phase: 'loading' }
  | { phase: 'setup' }
  | { phase: 'login'; needsTotp: boolean }
  | { phase: 'authed'; userId: number };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ phase: 'loading' });

  const refresh = useCallback(async () => {
    const status = await api<AuthStatus>('/api/auth/status');
    const me = await fetch('/api/auth/me');
    if (me.ok) {
      // An authed operator who lands on /setup (a reload, a stale tab) gets
      // the app, not the setup form — the server would refuse the claim
      // anyway, so showing it would only be a dead end.
      const current = (await me.json()) as AuthMe;
      setAuth({ phase: 'authed', userId: current.user.id });
      return;
    }
    // Consume the unused error body so no-store auth probes finish in the browser.
    await me.arrayBuffer();
    clearMeetingCaches();
    clearOverviewCaches();
    clearNavState(getSessionStorage());
    if (window.location.pathname === '/setup') {
      setAuth({ phase: 'setup' });
      return;
    }
    setAuth({ phase: 'login', needsTotp: status.needsTotp });
  }, []);

  useEffect(() => {
    refresh().catch(() => setAuth({ phase: 'login', needsTotp: false }));
  }, [refresh]);

  useEffect(() => {
    const onUnauthorized = () => {
      clearOverviewCaches();
      clearMeetingCaches();
      clearNavState(getSessionStorage());
      setAuth({ phase: 'login', needsTotp: false });
    };
    window.addEventListener('ss:unauthorized', onUnauthorized);
    return () => window.removeEventListener('ss:unauthorized', onUnauthorized);
  }, []);

  // Reconcile device-local caches against D1 once we know we're signed in.
  useEffect(() => {
    if (auth.phase === 'authed') {
      void syncMoneyMode();
      void syncSidebarLayout();
    }
  }, [auth.phase]);

  if (auth.phase === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="f-label">Loading…</span>
      </div>
    );
  }

  if (auth.phase === 'setup') {
    return <Setup onDone={() => refresh().catch(() => setAuth({ phase: 'login', needsTotp: false }))} />;
  }

  if (auth.phase === 'login') {
    return (
      <Login
        needsTotp={auth.needsTotp}
        onDone={() => {
          clearMeetingCaches();
          clearNavState(getSessionStorage());
          refresh().catch(() => setAuth({ phase: 'login', needsTotp: false }));
        }}
      />
    );
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/overview" replace />} />
        <Route path="/overview" element={<Overview userId={auth.userId} />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/outreach" element={<Outreach />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/crew" element={<Crew />} />
        <Route path="/drives" element={<Drives />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/meetings" element={<Meetings />} />
        <Route path="/meetings/:id" element={<MeetingDetailPage />} />
        <Route path="/finance" element={<Finance />} />
        <Route path="/finance/invoices/:id" element={<InvoiceDetail />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/dashboards" element={<Dashboards />} />
        <Route path="/dashboards/:slug" element={<DashboardEdit />} />
        <Route path="/social" element={<Social />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/systems/*" element={<Suspense fallback={<div className="empty-note">Loading systems…</div>}><Systems /></Suspense>} />
        <Route path="/marketing/*" element={<Suspense fallback={<div className="empty-note">Loading marketing…</div>}><Marketing /></Suspense>} />
        <Route path="/tools/:tool" element={<ToolEmbed />} />
        <Route path="/automations" element={<Automations />} />
        <Route
          path="/settings"
          element={
            <Settings
              onSignedOut={() => {
                clearOverviewCaches();
                clearMeetingCaches();
                clearNavState(getSessionStorage());
                refresh().catch(() => {});
              }}
            />
          }
        />
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </Shell>
  );
}
